/** Column definitions for the api-requests grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Sortable
 * columns match the server's ordering whitelist (id/created_ts — indexed
 * columns only). Cell popovers (CellActionsPopover) carry copy actions, links
 * and quick filters — `onQuickFilter` appends an exact chip to the search;
 * quick filters exist ONLY for the indexed namespaces.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Link as RouterLink } from 'react-router-dom';

import type { ApiRequestListItem } from '@/api/apiRequests';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TsValue } from '@/features/bgtasks/taskFormat';

import {
  StatusCodeChip,
  apiRequestStoreLabel,
  apiRequestTypeLabel,
  formatMs,
} from './apiRequestFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

/** The non-searchable booking tags the webhook carried, merged into one
 *  multiline "Booking" column (each line keeps its copy popover). */
const SUMMARY_FIELDS: { key: 'booking_contact' | 'booking_product' | 'booking_datetime' | 'booking_tickets'; label: string }[] = [
  { key: 'booking_contact', label: 'Contact' },
  { key: 'booking_product', label: 'Product' },
  { key: 'booking_datetime', label: 'Date' },
  { key: 'booking_tickets', label: 'Tickets' },
];

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to a request's detail dialog (keeps the grid's query string). */
  detailPath: (id: number) => string;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<ApiRequestListItem>[] {
  const { showTenant, onQuickFilter, detailPath } = opts;

  const cols: GridColDef<ApiRequestListItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Open request">
          <IconButton
            size="small"
            component={RouterLink}
            to={detailPath(p.row.id)}
            aria-label="Open request"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'created_ts',
      headerName: 'Received',
      width: 190,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.created_ts}
          actions={[
            copyAction('Copy timestamp', p.row.created_ts),
            // created's equality operator matches a whole UTC day.
            filterAction('Filter by this day (UTC)', 'created', p.row.created_ts.slice(0, 10), onQuickFilter),
          ]}
        >
          <TsValue value={p.row.created_ts} />
        </CellActionsPopover>
      ),
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 160,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tenant?.name ?? '—',
      renderCell: (p) =>
        p.row.tenant ? (
          <WithTenantMark environment={p.row.tenant.environment} placement="under">
            <CellActionsPopover
              auto
              actions={[
                {
                  key: 'open',
                  label: 'Open tour operator',
                  icon: <OpenInNewIcon fontSize="small" />,
                  href: `/tour-operators/${p.row.tenant.id}`,
                },
                filterAction('Filter by this tenant', 'tenant', String(p.row.tenant.id), onQuickFilter),
              ]}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.tenant.name}
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    });
  }

  cols.push(
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={p.row.name ?? undefined}
        >
          {p.row.name || '—'}
        </Box>
      ),
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 84,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.method || '—'}</Box>,
    },
    {
      field: 'status_code',
      headerName: 'Status',
      width: 88,
      filterable: false,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => row.status_code,
      renderCell: (p) => <StatusCodeChip code={p.row.status_code} />,
    },
    {
      field: 'integration',
      headerName: 'Integration',
      width: 110,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.integration ? (
          <CellActionsPopover
            auto
            actions={[
              copyAction('Copy integration', p.row.integration),
              filterAction('Filter by this integration', 'integration', p.row.integration, onQuickFilter),
            ]}
          >
            <Box component="span" sx={mono}>{p.row.integration}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => apiRequestTypeLabel(row.type),
      renderCell: (p) =>
        p.row.type ? (
          <CellActionsPopover
            auto
            actions={[
              filterAction('Filter by this type', 'type', apiRequestTypeLabel(p.row.type), onQuickFilter),
            ]}
          >
            {apiRequestTypeLabel(p.row.type)}
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'booking_id',
      headerName: 'Booking ID',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.booking_id ? (
          <CellActionsPopover
            auto
            title={p.row.booking_id}
            actions={[
              copyAction('Copy booking ID', p.row.booking_id),
              filterAction('Filter by this booking', 'booking', p.row.booking_id, onQuickFilter),
              {
                key: 'imports',
                label: 'Find in import runs',
                icon: <OpenInNewIcon fontSize="small" />,
                href: `/import-runs?q=${encodeURIComponent(`booking=${p.row.booking_id}`)}`,
              },
            ]}
          >
            <Box component="span" sx={mono}>{p.row.booking_id}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'booking_summary',
      headerName: 'Booking',
      flex: 1,
      minWidth: 220,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        SUMMARY_FIELDS.map((f) => row[f.key]).filter(Boolean).join(' · '),
      renderCell: (p) => {
        const lines = SUMMARY_FIELDS.filter((f) => p.row[f.key]);
        if (lines.length === 0) {
          return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
        }
        return (
          <Box sx={{ py: 0.5, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {lines.map((f) => {
              const value = p.row[f.key]!;
              return (
                <CellActionsPopover
                  key={f.key}
                  auto
                  title={value}
                  actions={[copyAction(`Copy ${f.label.toLowerCase()}`, value)]}
                  sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <Box component="span" sx={{ fontSize: 12, lineHeight: 1.35 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{f.label} </Box>
                    {value}
                  </Box>
                </CellActionsPopover>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'booking_status',
      headerName: 'Booking status',
      width: 130,
      filterable: false,
      sortable: false,
      renderCell: (p) => p.row.booking_status || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>,
    },
    {
      field: 'error_series_id',
      headerName: 'Errors',
      width: 110,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.error_series_id,
      renderCell: (p) =>
        p.row.error_series_id ? (
          <CellActionsPopover
            auto
            title={p.row.error_series_id}
            actions={[
              copyAction('Copy error series', p.row.error_series_id),
              filterAction('Filter by this error series', 'error_series', p.row.error_series_id, onQuickFilter),
              filterAction('Filter: has errors', 'errors', 'yes', onQuickFilter),
            ]}
          >
            <Chip size="small" color="error" label={p.row.error_series_id} sx={{ height: 20, fontSize: 11, fontFamily: 'monospace' }} />
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      // The actual failure text (already serialized) — triage failed webhooks without
      // opening the row. Default-hidden; enable when filtering to errors.
      field: 'error',
      headerName: 'Error',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.error,
      renderCell: (p) =>
        p.row.error ? (
          <Tooltip title={p.row.error}>
            <Box
              component="span"
              sx={{ color: 'error.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.error}
            </Box>
          </Tooltip>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      // Which Django view resolved the request — endpoint-level debugging.
      field: 'view',
      headerName: 'View',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.view || '—'}</Box>,
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 96,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => row.duration_ms,
      renderCell: (p) => (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMs(p.row.duration_ms)}
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 110,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={String(p.row.id)}
          actions={[
            copyAction('Copy request id', String(p.row.id)),
            {
              key: 'open',
              label: 'Open request',
              icon: <OpenInNewIcon fontSize="small" />,
              href: detailPath(p.row.id),
            },
          ]}
        >
          <Box component="span" sx={mono}>{p.row.id}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'request_id',
      headerName: 'Request ID',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.request_id ? (
          <CellActionsPopover
            auto
            title={p.row.request_id}
            actions={[
              copyAction('Copy request ID', p.row.request_id),
              filterAction('Filter by this request', 'request', p.row.request_id, onQuickFilter),
            ]}
          >
            <Box component="span" sx={mono}>{p.row.request_id}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'uuid',
      headerName: 'UUID',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={p.row.uuid}
          actions={[
            copyAction('Copy UUID', p.row.uuid),
            filterAction('Filter by this UUID', 'uuid', p.row.uuid, onQuickFilter),
          ]}
        >
          <Box component="span" sx={mono}>{p.row.uuid.slice(0, 8)}…</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'store',
      headerName: 'Store policy',
      width: 150,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => apiRequestStoreLabel(row.store),
      renderCell: (p) =>
        p.row.store != null ? (
          <CellActionsPopover
            auto
            actions={[
              filterAction('Filter by this store policy', 'store', apiRequestStoreLabel(p.row.store), onQuickFilter),
            ]}
          >
            {apiRequestStoreLabel(p.row.store)}
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'ip',
      headerName: 'Origin IP',
      width: 130,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.ip || '—'}</Box>,
    },
    {
      field: 'url',
      headerName: 'URL',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ ...mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.row.url ?? undefined}>
          {p.row.url || '—'}
        </Box>
      ),
    },
  );

  return cols;
}
